// src/modules/obras/components/CambiosOPModal.js
//
// Cuando se actualiza el presupuesto de una obra, Ceci adjunta el OP nuevo
// completo. Este modal compara el OP anterior (foto guardada en obra.opAnterior)
// contra el nuevo y clasifica los cambios en tres grupos:
//   ➕ agregados  → productos nuevos o mayor cantidad → se pide la diferencia
//   ➖ reducidos  → menor cantidad o quitados → informativo (no se pide)
//   ✓ iguales    → sin cambios
// Si hay agregados, ofrece generar el pedido complementario (solo las diferencias).

import React, { useEffect, useState } from "react";

const CAT_LABEL = {
  finnegans: "Perfil / refuerzo",
  perfiles: "Perfil / refuerzo",
  karpen: "Accesorio Karpen",
  plaza: "Accesorio de plaza",
  vidrios: "Vidrio",
  telas: "Tela mosquitera",
  terceros: "Tercero",
};

function nombreItem(it) {
  const cat = CAT_LABEL[it.categoria] || "";
  const desc = (it.detalle || it.alias || it.codNum || "").toString().slice(0, 60);
  return { cat, desc };
}

export default function CambiosOPModal({ obra, onClose, onGenerar, onMarcarRevisado }) {
  const [estado, setEstado] = useState("cargando"); // cargando | listo | error
  const [error, setError] = useState("");
  const [diff, setDiff] = useState(null); // { agregados, reducidos, iguales }

  useEffect(() => {
    (async () => {
      try {
        const opNuevo = obra?.archivos?.op;
        const opAnterior = obra?.opAnterior;
        if (!opNuevo || !opAnterior) throw new Error("Falta el OP nuevo o la foto del OP anterior");

        const { extraerItemsDeAdjunto, compararOPs } = await import("../services/parseArchivosObra");
        const [itemsAntes, itemsAhora] = await Promise.all([
          extraerItemsDeAdjunto(opAnterior, "op"),
          extraerItemsDeAdjunto(opNuevo, "op"),
        ]);
        setDiff(compararOPs(itemsAntes, itemsAhora));
        setEstado("listo");
      } catch (e) {
        setError(e.message || String(e));
        setEstado("error");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hayAgregados = diff && diff.agregados.length > 0;

  const rowStyle = { display: "grid", gridTemplateColumns: "1fr auto", gap: 8, alignItems: "center", fontSize: 12.5, padding: "4px 0", borderBottom: "1px solid #f1f5f9" };

  return (
    <div
      onClick={onClose}
      style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 290, padding: 16 }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{ background: "#fff", borderRadius: 12, padding: 18, width: "100%", maxWidth: 720, maxHeight: "88vh", overflowY: "auto", boxShadow: "0 10px 40px rgba(0,0,0,0.2)" }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <h3 style={{ margin: 0, fontSize: 16 }}>Cambios del OP — Obra {obra.numero}</h3>
          <button className="btn btn--ghost" onClick={onClose} style={{ fontSize: 18, lineHeight: 1 }}>×</button>
        </div>

        <div style={{ fontSize: 12.5, color: "#64748b", marginBottom: 10 }}>
          Se actualizó el presupuesto y el OP. Esto es lo que cambió respecto al pedido anterior.
        </div>

        {estado === "cargando" && <div style={{ color: "#64748b", fontSize: 13 }}>Comparando el OP anterior con el nuevo…</div>}
        {estado === "error" && <div style={{ color: "#b91c1c", fontSize: 13 }}>⚠ {error}</div>}

        {estado === "listo" && diff && (
          <>
            {/* ➕ Agregados */}
            <div style={{ background: "#f0fdf4", borderRadius: 8, padding: "8px 10px", marginBottom: 8 }}>
              <div style={{ fontWeight: 700, fontSize: 13, color: "#15803d", marginBottom: 4 }}>
                ➕ A pedir de más ({diff.agregados.length})
              </div>
              {diff.agregados.length === 0 ? (
                <div style={{ fontSize: 12, color: "#94a3b8" }}>No hay productos ni cantidades nuevas.</div>
              ) : diff.agregados.map((it, i) => {
                const { cat, desc } = nombreItem(it);
                return (
                  <div key={i} style={rowStyle}>
                    <span>{cat ? <b>{cat}: </b> : null}{desc}</span>
                    <span style={{ color: "#15803d", fontWeight: 700, whiteSpace: "nowrap" }}>
                      +{it.delta}{it.cantAntes > 0 ? <span style={{ fontWeight: 400, color: "#94a3b8" }}> ({it.cantAntes}→{it.cantAhora})</span> : <span style={{ fontWeight: 400, color: "#94a3b8" }}> (nuevo)</span>}
                    </span>
                  </div>
                );
              })}
            </div>

            {/* ➖ Reducidos */}
            {diff.reducidos.length > 0 && (
              <div style={{ background: "#fef2f2", borderRadius: 8, padding: "8px 10px", marginBottom: 8 }}>
                <div style={{ fontWeight: 700, fontSize: 13, color: "#b91c1c", marginBottom: 4 }}>
                  ➖ Bajó / se quitó ({diff.reducidos.length}) · no se pide nada
                </div>
                {diff.reducidos.map((it, i) => {
                  const { cat, desc } = nombreItem(it);
                  return (
                    <div key={i} style={rowStyle}>
                      <span>{cat ? <b>{cat}: </b> : null}{desc}</span>
                      <span style={{ color: "#b91c1c", whiteSpace: "nowrap" }}>
                        {it.cantAhora <= 0 ? "quitado" : `${it.cantAntes}→${it.cantAhora}`}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}

            {/* ✓ Iguales (resumen) */}
            {diff.iguales.length > 0 && (
              <div style={{ fontSize: 12, color: "#94a3b8", marginBottom: 10 }}>
                ✓ {diff.iguales.length} producto(s) sin cambios.
              </div>
            )}

            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", flexWrap: "wrap", marginTop: 6 }}>
              <button className="btn btn--ghost" onClick={() => onMarcarRevisado()} style={{ fontSize: 13 }}>
                {hayAgregados ? "No pedir nada — marcar revisado" : "Marcar revisado"}
              </button>
              {hayAgregados && (
                <button className="btn" onClick={() => onGenerar(diff.agregados)} style={{ fontSize: 13, background: "#16a34a", color: "#fff", border: "none" }}>
                  Generar pedido complementario ({diff.agregados.length})
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
